import { Card, Typography } from '@material-ui/core'
import { cloneElement } from 'react'
import {
  ArrayField,
  EditButton,
  FileField,
  FunctionField,
  Labeled,
  NumberField,
  SingleFieldList,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useShowController,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { Text } from 'shared/components/Text/Text'

import { BrokerSubmissionMethods } from 'helpers/constants'
import { getCountryNameFromCode } from 'helpers/countries'
import { useHasBrokerCarrierRelationship } from 'hooks/useHasFeatureFlag'
import { BrokerCategoryKey, BrokerCategoryName, BrokerCreditAttribute } from '../../../src/models/jobs'
import SuccessIconFilled from '../../assets/images/SuccessIconFilled.svg'
import Aside from '../Aside/aside'
import { BrokerCreditRatingLabel } from '../Common/BrokerCreditRatingLabel'
import { CustomBooleanField, LabeledTypographyField, SimpleChipField } from '../Fields'
import BrokerShowV2 from './BrokerShowV2'
import { BrokerStatus } from './BrokerStatusTransitions'

const BrokerShowActions = ({ basePath, data }: any) => {
  return (
    <TopToolbar>
      <EditButton basePath={basePath} record={data} />
    </TopToolbar>
  )
}

const getBrokerStatusIcon = (status: any) => {
  return status && status === BrokerStatus.Approved.id ? (
    <img src={SuccessIconFilled} alt="" width="16px" style={{ margin: '-3px 0 0 -5px' }} />
  ) : null
}

const BrokerStatusField = ({ record, status, mcNumber, label }: any) => {
  return record ? (
    <Labeled label={label}>
      <Typography variant="body2" component="span">
        {getBrokerStatusIcon(status)} {status} - {mcNumber}
      </Typography>
    </Labeled>
  ) : null
}

export const CustomBrokerShow = (props: any) => {
  const { basePath, loaded, record, resource, version } = useShowController(props)
  const hasBrokerCarrierRelationship = useHasBrokerCarrierRelationship()
  usePageTitle(record?.name)

  return loaded ? (
    <FlexBox isColumn gap="12px">
      <BrokerShowActions basePath={basePath} data={record} />
      <Typography variant="h4">{record?.name}</Typography>
      {!hasBrokerCarrierRelationship && record?.carrier_portal_link && (
        <LabeledTypographyField label="Carrier Portal" variant="body2" value={record.carrier_portal_link} />
      )}
      <FlexBox styles="width: 50%;" justifyContent="space-between">
        {!hasBrokerCarrierRelationship && (
          <>
            <BrokerStatusField
              record={record}
              status={record?.status}
              label="Status for old MC"
              mcNumber="1046672"
            />
            <BrokerStatusField
              record={record}
              status={record?.mc_23_status}
              label="Status for new MC"
              mcNumber="1532905"
            />
          </>
        )}
        {hasBrokerCarrierRelationship && (
          <FlexBox gap="10px" alignItems="center">
            <Text variant="paragraph1">MC {record?.mc_number}</Text>
            {record?.dot_number && (
              <>
                <Text variant="paragraph1">|</Text>
                <Text variant="paragraph1">DOT {record?.dot_number}</Text>
              </>
            )}
            <Text variant="paragraph1">|</Text>
            <BrokerCreditRatingLabel
              creditRating={record?.credit_rating}
              creditRatingCategory={record?.credit_rating_category[BrokerCategoryKey.CREDIT_RATING]}
              creditRatingCategoryName={BrokerCategoryName.CREDIT_RATING}
              paddingScore={'0px'}
              paddingName={'0px'}
              isHorizontal
            />
            <BrokerCreditRatingLabel
              creditRating={record?.credit_rating_metadata[BrokerCreditAttribute.PAST_DUE_AR_PCT]}
              creditRatingCategory={record?.credit_rating_category[BrokerCategoryKey.PAST_DUE_AR_PCT]}
              creditRatingCategoryName={BrokerCategoryName.PAST_DUE_AR_PCT}
              paddingScore={'0px'}
              paddingName={'0px'}
              isHorizontal
            />
          </FlexBox>
        )}
      </FlexBox>
      <Card style={{ marginTop: '20px' }}>
        {cloneElement(props.children, {
          basePath,
          record,
          resource,
          version,
        })}
      </Card>
      <Aside record={record} resource="brokers" />
    </FlexBox>
  ) : null
}

const BrokerShowV1 = (props: any) => {
  const { record } = useShowController(props)
  if (!record) return null

  return (
    <CustomBrokerShow {...props}>
      <TabbedShowLayout>
        <Tab label="Overview">
          <TextField source="dot_number" />
          <CustomBooleanField source="is_shipper" />
          {record?.is_shipper ? (
            <LabeledTypographyField label="FEIN Number" variant="body2" value={record.fein_number} />
          ) : (
            <LabeledTypographyField label="MC Number" variant="body2" value={record.mc_number} />
          )}
          <BrokerCreditRatingLabel
            creditRating={record?.credit_rating}
            creditRatingCategory={record?.credit_rating_category[BrokerCategoryKey.CREDIT_RATING]}
            creditRatingCategoryName={BrokerCategoryName.CREDIT_RATING}
            paddingScore={'0px'}
            paddingName={'0px'}
          />
          <BrokerCreditRatingLabel
            creditRating={record?.credit_rating_metadata[BrokerCreditAttribute.PAST_DUE_AR_PCT]}
            creditRatingCategory={record?.credit_rating_category[BrokerCategoryKey.PAST_DUE_AR_PCT]}
            creditRatingCategoryName={BrokerCategoryName.PAST_DUE_AR_PCT}
            paddingScore={'0px'}
            paddingName={'0px'}
          />
          <ArrayField source="booking_types">
            <SingleFieldList>
              <SimpleChipField record={record} />
            </SingleFieldList>
          </ArrayField>
          <TextField source="carrier_portal_link" label="Invoicing Portal Link" />
          <CustomBooleanField source="is_carrier_packet_complete" label="Old MC Carrier packet complete?" />
          <FileField source="carrier_agreement" title="View" label="Old MC Carrier agreement" />
          <TextField source="status_reason" label="Old MC Status reason" />
          <CustomBooleanField source="trucklists_enabled" label="Trucklists Enabled" />
          <TextField source="name" />
          <TextField source="id" />
        </Tab>
        <Tab label="Billing address">
          <FunctionField
            label="Country"
            render={(record: any) => getCountryNameFromCode(record?.billing_address?.country)}
          />
          <TextField source="billing_address.line1" label="Line 1" />
          <TextField source="billing_address.line2" label="Line 2" />
          <TextField source="billing_address.city" label="City" />
          <TextField source="billing_address.state" label="State" />
          <TextField source="billing_address.zip" label="Zip" />
        </Tab>
        <Tab label="Contact info">
          <TextField source="support_main_phone" />
          <TextField source="support_main_email" />
          <CustomBooleanField
            source="use_support_email_for_tracking"
            label="Use to send job tracking information"
          />
          <TextField source="bol_contact_email" label="VC Invoicing Email" />
          <TextField source="factoring_invoicing_email" label="Factoring Invoicing Email" />
          <TextField source="factoring_noa_email" label="Factoring NOA Email" />
          <TextField source="pay_status_inquiry_email" label="Pay Status Inquiry Email" />
          <TextField source="support_after_hours_phone" />
          <TextField source="support_after_hours_email" />
          <TextField source="booking_contact_phone" />
          <TextField source="booking_contact_email" />
          <TextField source="trucklists_email" label="Trucklists Email" />
        </Tab>
        <Tab label="Invoicing">
          <TextField source="bol_submission_method" label="Invoice Submission Method" />
          {record?.bol_submission_method === BrokerSubmissionMethods.Email && (
            <CustomBooleanField
              source="is_invoicing_submission_automated"
              label="Automated Invoicing Submission"
            />
          )}
          <TextField source="tracking_submission_method" label="Job Tracking Submission Method" />
          <NumberField source="payment_terms_days" />
          <TextField source="payment_method" />
          <NumberField source="layover_rate_per_day" />
          <NumberField source="detention_rate_per_hour" />
          <NumberField source="detention_start_time_hours" />
          <NumberField source="detention_max_hours_per_day" />
          <NumberField source="tonu_rate" />
          <TextField
            source="submission_requirement_notes"
            label="Invoicing Submission Requirements"
            fullWidth
            component="pre"
          />
        </Tab>
        <Tab label="Notes">
          <TextField source="notes" fullWidth component="pre" />
        </Tab>
      </TabbedShowLayout>
    </CustomBrokerShow>
  )
}

export const BrokerShow = (props: any) => {
  const hasBrokerCarrierRelationship = useHasBrokerCarrierRelationship()
  if (hasBrokerCarrierRelationship) {
    return <BrokerShowV2 {...props} />
  }
  return <BrokerShowV1 {...props} />
}
